import { forwardRef, useState } from "react";
import type { InputHTMLAttributes } from "react";
import { Eye, EyeOff } from "lucide-react";
import clsx from "clsx";

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, label, error, id, type, ...props }, ref) => {
    const inputId = id ?? props.name;
    const errorId = error && inputId ? `${inputId}-error` : undefined;
    const isPassword = type === "password";
    const [visible, setVisible] = useState(false);

    return (
      <label className="flex flex-col gap-2 text-sm" htmlFor={inputId}>
        {label && <span className="font-medium text-ink-muted">{label}</span>}
        <div className="relative">
          <input
            ref={ref}
            id={inputId}
            type={isPassword ? (visible ? "text" : "password") : type}
            aria-invalid={!!error}
            aria-describedby={errorId}
            className={clsx(
              "h-12 w-full rounded-xl border border-border bg-surface px-4 text-ink placeholder:text-ink-muted/60",
              "transition-all duration-300 ease-physical focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent",
              isPassword && "pr-11",
              error && "border-red-500/60 focus:ring-red-500/30",
              className
            )}
            {...props}
          />
          {isPassword && (
            <button
              type="button"
              onClick={() => setVisible((v) => !v)}
              tabIndex={-1}
              aria-label={visible ? "Hide password" : "Show password"}
              className="absolute right-0 top-0 h-12 w-11 flex items-center justify-center text-ink-muted hover:text-ink transition-colors duration-300"
            >
              {visible ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          )}
        </div>
        {error && (
          <span id={errorId} className="text-xs text-red-500">
            {error}
          </span>
        )}
      </label>
    );
  }
);
Input.displayName = "Input";
